import React, { useCallback, useEffect, useState } from "react";
import { X, Search, ImagePlus, UploadCloud, Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

/**
 * ImagePickerModal — pick an image from the Image Manager.
 *
 * Props:
 *   token          — admin JWT
 *   onSelect(url)  — called with the ABSOLUTE public URL (e.g. https://.../uploads/xyz.webp)
 *   onClose()      — dismiss without picking
 *   returnFormat   — "absolute" (default) or "relative" (/uploads/xxx.webp)
 */
export function ImagePickerModal({ token, onSelect, onClose, returnFormat = "absolute" }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [facets, setFacets] = useState({ folders: [], tags: [] });
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const auth = { Authorization: `Bearer ${token}` };
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: "1", page_size: "120" });
    if (q.trim()) params.set("q", q.trim());
    if (folder) params.set("folder", folder);
    const r = await fetch(`${API_BASE}/api/admin/images?${params}`, { headers: auth });
    if (!r.ok) return;
    const data = await r.json();
    setItems(data.items || []);
  }, [q, folder, token]); // eslint-disable-line

  const loadFacets = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/images/facets`, { headers: auth });
    if (r.ok) setFacets(await r.json());
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFacets(); }, [loadFacets]);

  const pick = (img) => {
    const url = returnFormat === "relative" ? img.path : `${origin}${img.path}`;
    onSelect(url);
    onClose?.();
  };

  const handleQuickUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const r = await fetch(`${API_BASE}/api/admin/images/bulk`, { method: "POST", headers: auth, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      if (data.saved?.length) {
        // Auto-pick the first uploaded image and close
        pick(data.saved[0]);
      } else if (data.errors?.length) {
        setUploadErr(data.errors[0].error || "Upload failed");
      }
    } catch (err) {
      setUploadErr(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-picker-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h3 className="text-base font-bold text-[#0a2a5e] flex items-center gap-2">
            <ImagePlus className="h-5 w-5" /> Pick an image
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="image-picker-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename or tag…" className="pl-9" data-testid="image-picker-search" />
          </div>
          <select value={folder} onChange={(e) => setFolder(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="image-picker-folder">
            <option value="">All folders</option>
            {facets.folders.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <label className={`rounded-full px-4 h-9 text-sm font-semibold flex items-center gap-1.5 cursor-pointer transition ${uploading ? "bg-slate-300 text-white cursor-wait" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`} data-testid="image-picker-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload new"}
            <input
              type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              disabled={uploading}
              onChange={(e) => handleQuickUpload(e.target.files)}
              className="hidden"
            />
          </label>
        </div>

        {uploadErr && <div className="mx-5 mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{uploadErr}</div>}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items === null && <div className="text-center text-slate-400 py-16">Loading…</div>}
          {items && items.length === 0 && (
            <div className="text-center py-16">
              <ImagePlus className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No images match. Try "Upload new" above.</p>
            </div>
          )}
          {items && items.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((img) => (
                <button
                  key={img.id}
                  onClick={() => pick(img)}
                  data-testid={`image-picker-item-${img.id}`}
                  className="group text-left bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-sky-500 hover:ring-2 hover:ring-sky-200 transition"
                  title={`${img.original_name} — ${img.width}×${img.height}`}
                >
                  <div className="aspect-square bg-slate-50 flex items-center justify-center">
                    <img src={img.path} alt={img.original_name} loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="px-2 py-1.5 border-t border-slate-100">
                    <div className="text-[11px] font-semibold text-slate-800 truncate">{img.original_name || img.filename}</div>
                    <div className="text-[10px] text-slate-500">{img.width}×{img.height}</div>
                  </div>
                  <div className="hidden group-hover:flex items-center justify-center gap-1 py-1.5 bg-sky-500 text-white text-xs font-semibold">
                    <Check className="h-3 w-3" /> Use this
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end bg-slate-50">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
